<template>
  <v-card flat class="mt-4 pa-4 time-input-card">
    <!-- Mode-Auswahl mit Chips -->
    <v-chip-group v-model="mode" row mandatory class="mb-4 timer-mode-chips">
      <v-chip value="stopwatch" small outlined class="ma-1">⏱ Stoppuhr</v-chip>
      <v-chip value="countdown" small outlined class="ma-1">⏳ Countdown</v-chip>
      <v-chip value="manual" small outlined class="ma-1">🔢 Manuell</v-chip>
    </v-chip-group>

    <!-- Timer-Anzeige (Stoppuhr & Countdown) -->
    <div v-if="mode !== 'manual'" class="text-center">
      <div class="display-time pa-3 mb-3 monospace">{{ formattedTime }}</div>

      <!-- Eingabe für Countdown nur vor Start -->
      <v-text-field
        v-if="mode === 'countdown' && !running"
        v-model.number="countdownSeconds"
        label="Startzeit (Sekunden)"
        type="number"
        dense
        outlined
        class="mb-3" />

      <!-- Steuerungs-Buttons -->
      <v-row justify="space-around" no-gutters>
        <v-col cols="4" class="d-flex justify-center">
          <v-btn color="success" @click="startTimer" :disabled="running">Start</v-btn>
        </v-col>
        <v-col cols="4" class="d-flex justify-center">
          <v-btn color="warning" @click="pauseTimer" :disabled="!running">Pause</v-btn>
        </v-col>
        <v-col cols="4" class="d-flex justify-center">
          <v-btn color="error" @click="resetTimer">Reset</v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Manuelle Eingabe -->
    <div v-else>
      <v-text-field v-model.number="manualSeconds" type="number" label="Dauer (Sekunden)" outlined dense />
    </div>

    <!-- Satz speichern -->
    <v-btn block class="mt-4" color="primary" :disabled="totalSeconds === 0" @click="submit"> Satz hinzufügen </v-btn>
  </v-card>
</template>

<script>
export default {
  name: "TimeInput",
  props: ["exercise"],
  data() {
    return {
      mode: "stopwatch", // 'stopwatch' | 'countdown' | 'manual'
      seconds: 0,
      hundredths: 0,
      timer: null,
      running: false,
      manualSeconds: null,
      countdownSeconds: 0,
    }
  },
  computed: {
    formattedTime() {
      const min = String(Math.floor(this.seconds / 60)).padStart(2, "0")
      const sec = String(this.seconds % 60).padStart(2, "0")
      const hund = String(this.hundredths).padStart(2, "0")
      return `${min}:${sec}.${hund}`
    },
    totalSeconds() {
      return this.mode === "manual" ? this.manualSeconds || 0 : this.seconds + this.hundredths / 100
    },
  },
  methods: {
    initCountdownDefaults() {
      if (this.mode !== "countdown") return
      // 1) Letztes gesetztes "Dauer" aus vorhandenen Sets
      const lastSet = Array.isArray(this.exercise.sets) && this.exercise.sets.length ? this.exercise.sets[this.exercise.sets.length - 1] : null
      if (lastSet) {
        const entry = lastSet.find((e) => e.text === "Dauer")
        if (entry && typeof entry.value === "number") {
          this.countdownSeconds = entry.value
          return
        }
      }
      // 2) Fallback: Default aus set_exercise_settings
      const setting = this.exercise.set_exercise_settings?.find((s) => s.text === "Dauer")
      const val = parseFloat(setting?.selections?.[0])
      this.countdownSeconds = !isNaN(val) ? val : 0
    },
    startTimer() {
      if (this.running) return

      if (this.mode === "countdown") {
        // initiale Werte setzen
        this.seconds = Math.floor(this.countdownSeconds)
        this.hundredths = Math.round((this.countdownSeconds % 1) * 100)
      }

      this.timer = setInterval(() => {
        if (this.mode === "stopwatch") {
          this.hundredths++
          if (this.hundredths >= 100) {
            this.hundredths = 0
            this.seconds++
          }
        } else if (this.mode === "countdown") {
          if (this.hundredths === 0) {
            if (this.seconds === 0) {
              this.pauseTimer()
              return
            }
            this.seconds--
            this.hundredths = 99
          } else {
            this.hundredths--
          }
        }
      }, 10)
      this.running = true
    },
    pauseTimer() {
      clearInterval(this.timer)
      this.running = false
    },
    resetTimer() {
      this.pauseTimer()
      this.seconds = 0
      this.hundredths = 0
      this.initCountdownDefaults()
    },
    submit() {
      this.pauseTimer()
      const value = Number(this.totalSeconds.toFixed(2))
      if (value > 0) {
        this.$emit("add-set", [{ text: "Dauer", value }])
        this.resetTimer()
        this.manualSeconds = null
      }
    },
  },
  watch: {
    mode(newVal) {
      if (newVal === "countdown") this.initCountdownDefaults()
    },
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  mounted() {
    this.initCountdownDefaults()
  },
}
</script>

<style scoped>
.display-time {
  font-size: 3rem;
  font-weight: bold;
  color: #ffffff;
  background: #1e1e1e;
  border-radius: 12px;
  padding: 16px;
  display: inline-block;
  font-family: monospace;
  letter-spacing: 1px;
  min-width: 180px;
  text-align: center;
}
.time-input-card {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}
</style>
